// src/canonical.ts
function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(stableStringify).join(",") + "]";
  const keys = Object.keys(value).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(value[k])).join(",") + "}";
}
function fnv1a32(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// src/tile.ts
import { z } from "zod";
var SUITS = ["m", "p", "s"];
var NUM = (s) => Array.from({ length: 9 }, (_, i) => `${s}${i + 1}`);
var ALL_TILE_IDS = [
  ...NUM("m"),
  ...NUM("p"),
  ...NUM("s"),
  "z1",
  "z2",
  "z3",
  "z4",
  "z5",
  "z6",
  "z7",
  // E S W N | white green red
  "m5r",
  "p5r",
  "s5r",
  // aka fives
  "z8",
  "z9",
  // optional extra winds (P4+)
  // 花牌 (flower / season bonus tiles) — OPT-IN only (rs.tiles.flowers). f1-4 = seasons 春夏秋冬,
  // f5-8 = flowers 梅蘭菊竹. Never part of the 4-melds+pair hand: drawn flowers are set aside as
  // +1 dora each and trigger a replacement draw (mirrors 北抜き / nukidora). Listed LAST so the
  // 136-tile wall is byte-identical when flowers are OFF (buildWall only appends them when ON).
  "f1",
  "f2",
  "f3",
  "f4",
  "f5",
  "f6",
  "f7",
  "f8"
];
var TileIdSchema = z.enum(
  ALL_TILE_IDS
);
var isHonor = (t) => t[0] === "z";
var isAka = (t) => t.endsWith("r");
var baseOfAka = (t) => isAka(t) ? t.slice(0, 2) : t;
var isFlower = (t) => t[0] === "f";
function sortKey(t) {
  const base = baseOfAka(t);
  const suit = base[0];
  const suitRank = suit === "m" ? 0 : suit === "p" ? 1 : suit === "s" ? 2 : suit === "z" ? 3 : 4;
  const num = Number(base.slice(1));
  return suitRank * 100 + num * 2 + (isAka(t) ? 1 : 0);
}
function compareTiles(a, b) {
  return sortKey(a) - sortKey(b);
}

// src/codec.ts
var MOVELOG_FORMAT = 1;
var KIND_CODES = {
  discard: 0,
  chii: 1,
  pon: 2,
  minkan: 3,
  ankan: 4,
  shouminkan: 5,
  tsumo: 6,
  ron: 7,
  pass: 8,
  kyuushuu: 9,
  kita: 10,
  windkan: 11,
  flowerpull: 12
};
var CODE_KINDS = Object.fromEntries(Object.entries(KIND_CODES).map(([k, v]) => [v, k]));
var TILE_INDEX = new Map(ALL_TILE_IDS.map((t, i) => [t, i]));
function pushVarint(out, n) {
  let v = n >>> 0;
  while (v >= 128) {
    out.push(v & 127 | 128);
    v >>>= 7;
  }
  out.push(v);
}
function readVarint(bytes, pos) {
  let result = 0, shift = 0, b;
  do {
    b = bytes[pos.i++];
    result |= (b & 127) << shift;
    shift += 7;
  } while (b & 128);
  return result >>> 0;
}
function packMoves(moves, format) {
  if (format !== MOVELOG_FORMAT) throw new Error(`unsupported movelog format ${format}`);
  const out = [];
  for (const m of moves) {
    out.push(KIND_CODES[m.kind]);
    if (m.kind === "discard") {
      pushVarint(out, TILE_INDEX.get(m.tile));
      out.push((m.riichi ? 1 : 0) | (m.openRiichi ? 2 : 0) | (m.tsumogiri ? 4 : 0) | (m.infiniteRiichi ? 8 : 0));
    } else if (m.kind === "chii") {
      pushVarint(out, TILE_INDEX.get(m.tiles[0]));
      pushVarint(out, TILE_INDEX.get(m.tiles[1]));
    } else if (m.kind === "ankan" || m.kind === "shouminkan" || m.kind === "flowerpull") {
      pushVarint(out, TILE_INDEX.get(m.tile));
    } else if (m.kind === "kita") {
      pushVarint(out, m.tile !== void 0 ? TILE_INDEX.get(m.tile) + 1 : 0);
    }
  }
  return Uint8Array.from(out);
}
function unpackMoves(bytes, format) {
  if (format !== MOVELOG_FORMAT) throw new Error(`unsupported movelog format ${format}`);
  const pos = { i: 0 };
  const moves = [];
  while (pos.i < bytes.length) {
    const kind = CODE_KINDS[bytes[pos.i++]];
    if (kind === void 0) throw new Error("bad kind code");
    if (kind === "discard") {
      const tile = ALL_TILE_IDS[readVarint(bytes, pos)];
      const flags = bytes[pos.i++];
      const m = { kind, tile };
      if (flags & 1) m.riichi = true;
      if (flags & 2) m.openRiichi = true;
      if (flags & 4) m.tsumogiri = true;
      if (flags & 8) m.infiniteRiichi = true;
      moves.push(m);
    } else if (kind === "chii") {
      moves.push({ kind, tiles: [ALL_TILE_IDS[readVarint(bytes, pos)], ALL_TILE_IDS[readVarint(bytes, pos)]] });
    } else if (kind === "ankan" || kind === "shouminkan" || kind === "flowerpull") {
      const m = { kind, tile: ALL_TILE_IDS[readVarint(bytes, pos)] };
      moves.push(m);
    } else if (kind === "kita") {
      const tileIdx = readVarint(bytes, pos);
      const m = tileIdx === 0 ? { kind } : { kind, tile: ALL_TILE_IDS[tileIdx - 1] };
      moves.push(m);
    } else {
      moves.push({ kind });
    }
  }
  return moves;
}

// src/context.ts
import { z as z3 } from "zod";

// src/seat.ts
import { z as z2 } from "zod";
var SeatSchema = z2.number().int().nonnegative();
var DiscardEntrySchema = z2.object({
  tile: TileIdSchema,
  tsumogiri: z2.boolean(),
  riichiTile: z2.boolean(),
  calledAway: z2.boolean()
});

// src/context.ts
var DetectionContextSchema = z3.object({
  winBy: z3.enum(["ron", "tsumo"]),
  firstTurn: z3.boolean(),
  lastTile: z3.enum(["none", "haitei", "houtei"]),
  isDealer: z3.boolean(),
  menzen: z3.boolean(),
  afterKan: z3.boolean()
});

// src/cosmetic.ts
import { z as z4 } from "zod";
var SharedCosmeticSchema = z4.object({
  // background / room dressing rendered OUTSIDE the play surface only. Optional + additive-friendly.
  background: z4.string().optional(),
  frame: z4.string().optional()
}).strict();
var LOCAL_ONLY_COSMETIC_KEYS = ["tileDesign", "tableColor"];
function isSharedCosmetic(v) {
  if (!SharedCosmeticSchema.safeParse(v).success) return false;
  return LOCAL_ONLY_COSMETIC_KEYS.every((k) => !(typeof v === "object" && v !== null && k in v));
}

// src/intent.ts
import { z as z5 } from "zod";
var IntentSchema = z5.discriminatedUnion("kind", [
  z5.object({ kind: z5.literal("discard"), tile: TileIdSchema, riichi: z5.boolean().optional(), openRiichi: z5.boolean().optional(), infiniteRiichi: z5.boolean().optional(), tsumogiri: z5.boolean().optional() }),
  z5.object({ kind: z5.literal("chii"), tiles: z5.tuple([TileIdSchema, TileIdSchema]) }),
  z5.object({ kind: z5.literal("pon") }),
  z5.object({ kind: z5.literal("minkan") }),
  z5.object({ kind: z5.literal("ankan"), tile: TileIdSchema }),
  z5.object({ kind: z5.literal("shouminkan"), tile: TileIdSchema }),
  z5.object({ kind: z5.literal("tsumo") }),
  z5.object({ kind: z5.literal("ron") }),
  z5.object({ kind: z5.literal("pass") }),
  z5.object({ kind: z5.literal("kyuushuu") }),
  // 北抜き / 西抜き (nukidora). `tile` names the pulled honor (default 'z4' = North when absent, so an
  // existing { kind:'kita' } intent is unchanged). 2-player (二麻) may pull West ('z3') when the ruleset
  // allows it via flags.nukiTiles.
  z5.object({ kind: z5.literal("kita"), tile: TileIdSchema.optional() }),
  // 東西南北の特殊暗槓 (異種4牌): kan the four winds (z1..z4, one each) as one concealed meld → forms the
  // 役牌(東西南北) yaku. No tile payload (the four winds are implied). Gated on the wind-set yaku.
  z5.object({ kind: z5.literal("windkan") }),
  // 花抜き (flower pull): set aside a DRAWN flower held in hand (花牌 OPT-IN). `tile` names the held flower
  // (f1..f8). Mirrors 北抜き: flowerCount++, draw a dead-wall replacement, stay in the same turn phase.
  z5.object({ kind: z5.literal("flowerpull"), tile: TileIdSchema })
]);
var ALL_INTENT_SAMPLES = [
  { kind: "discard", tile: "m1" },
  { kind: "discard", tile: "p5r", riichi: true, openRiichi: true, tsumogiri: true },
  { kind: "chii", tiles: ["m1", "m2"] },
  { kind: "pon" },
  { kind: "minkan" },
  { kind: "ankan", tile: "s3" },
  { kind: "shouminkan", tile: "s3" },
  { kind: "tsumo" },
  { kind: "ron" },
  { kind: "pass" },
  { kind: "kyuushuu" },
  { kind: "kita" },
  { kind: "kita", tile: "z3" },
  { kind: "flowerpull", tile: "f1" }
];

// src/meld.ts
import { z as z6 } from "zod";
var MELD_KINDS = ["chii", "pon", "minkan", "ankan", "shouminkan", "kita"];
var MeldSchema = z6.object({
  kind: z6.enum(MELD_KINDS),
  tiles: z6.array(TileIdSchema),
  calledFrom: SeatSchema.nullable(),
  calledTileIndex: z6.number().int().nonnegative()
});

// src/ruleset.ts
import { z as z7 } from "zod";
var RULESET_SCHEMA_VERSION = 1;
var CATALOG_SCHEMA_VERSION = 1;
var RuleSetSchema = z7.object({
  version: z7.number().int(),
  players: z7.union([
    z7.literal(1),
    z7.literal(2),
    z7.literal(3),
    z7.literal(4),
    z7.literal(5),
    z7.literal(6),
    z7.literal(7)
  ]),
  tiles: z7.object({
    suits: z7.array(z7.enum(["m", "p", "s"])),
    honors: z7.array(z7.enum(["E", "S", "W", "N", "P", "F", "C"])),
    akaCount: z7.record(z7.enum(["m", "p", "s"]), z7.number().int()),
    removeManzu2to8: z7.boolean().optional(),
    extraWindMode: z7.enum(["standard4", "extendWinds", "repeatNorth"]).optional(),
    // 客風牌の除外 (人数連動): OPT-IN. When ON, wind honors that are nobody's seat wind for the table's
    // player count (`players`) are dropped from the wall — 1p keeps 東, 2p 東南, 3p 東南西, 4p+ 東南西北.
    // A 抜きドラ target wind (sanmaKitaMode==='nukiDora', e.g. 北 in 3麻) is ALWAYS kept so 北抜き/西抜き
    // tiles still exist. Dragons (白發中) are never dropped. Absent (default) = OFF = every configured
    // honor stays → wall byte-identical (determinism golden digest unchanged). Caveat: a match whose round
    // wind advances past the kept winds (e.g. a 2-player West round) then has zero tiles of that round wind.
    dropGuestWinds: z7.boolean().optional(),
    // 無限牌: tiles are sampled WITH REPLACEMENT — the live wall never empties (so no exhaustive draw /
    // ryuukyoku), and the same tile can be drawn 5+ times (enabling same-tile multi-kan). Opening deal
    // is still from a normal shuffle; only ongoing draws/rinshan/kan-dora are infinite.
    infinite: z7.boolean().optional(),
    // 花牌 (flower + season bonus tiles, f1..f8): OPT-IN. Default-absent (= OFF) keeps the wall at
    // exactly 136 tiles and every existing behavior byte-identical. When ON, buildWall appends one of
    // each f1..f8 (→ 144), a DRAWN flower is set aside (never kept in hand) as +1 dora and triggers a
    // replacement draw (mirrors 北抜き / nukidora). Flowers are never part of the 4-melds+pair hand.
    // TODO: 花牌-specific yaku (七搶一 / 花和 / 八仙) are deferred — only the bonus-dora core is here.
    flowers: z7.boolean().optional(),
    // ドラ表示牌固定 (testing/house): force the revealed dora INDICATORS in order — entry[0] is the
    // opening indicator, entry[1] the 1st kan-dora, etc. Falls back to the dead wall / RNG once the list
    // runs out. Absent (default) = normal random reveal (byte-identical). Indicators, not the dora itself.
    fixedDoraIndicators: z7.array(TileIdSchema).optional(),
    // 初期自摸固定 (testing/house): force the tiles SEAT 0 (the human) draws, in order — draw[0] is the
    // first tsumo, etc. Falls back to the wall once the list runs out. Bypasses the wall + flower
    // set-aside (testing convenience). Absent (default) = normal draws (byte-identical).
    fixedDraws: z7.array(TileIdSchema).optional()
  }),
  enabledYaku: z7.record(z7.string(), z7.object({ enabled: z7.boolean(), hanOverride: z7.number().int().optional() })),
  scoring: z7.object({
    // `aotenjou` is the legacy boolean (true == old no-limit mode). It is superseded by `limitMode`
    // but kept for back-compat: the engine derives the effective mode via
    // `limitMode ?? (aotenjou ? 'aotenjou' : 'capped')`, so old persisted rulesets still work.
    aotenjou: z7.boolean().optional(),
    limitMode: z7.enum(["capped", "aotenjou", "stepped"]).optional(),
    kiriageMangan: z7.boolean().optional(),
    kazoeYakumanCap: z7.number().int(),
    doubleYakuman: z7.boolean().optional(),
    tripleYakuman: z7.boolean().optional(),
    fuRounding: z7.union([z7.literal(10), z7.literal(1)]).optional(),
    // EASY scoring toggles (all default-on / standard when omitted).
    uraDora: z7.boolean().optional(),
    // count ura-dora on a riichi win
    kanDora: z7.boolean().optional(),
    // reveal kan-dora indicators when a kan is made
    hanRequirement: z7.union([z7.literal(1), z7.literal(2), z7.literal(4)]).optional(),
    // 翻縛り (min yaku-han)
    doubleWindPairFu: z7.union([z7.literal(2), z7.literal(4)]).optional(),
    // 連風牌 pair fu
    // Additive scoring toggles (absent == current behavior; intentionally NOT in defaultRuleSet).
    yakumanStacking: z7.enum(["exclusive", "withNormalYaku"]).optional(),
    // absent/'exclusive' = yakuman suppresses normal yaku; 'withNormalYaku' keeps them
    exclusionOverrides: z7.array(z7.tuple([z7.string(), z7.string()])).optional(),
    // [keeperId, keptAlsoId]: when keeperId present, do NOT drop keptAlsoId via .excludes
    doubleWindTripletHan: z7.union([z7.literal(1), z7.literal(2)]).optional(),
    // absent/2 = 連風牌 triplet counts場風+自風 (2 han); 1 = count once
    // ドラ隣 (kamisama-mahjong): tiles ADJACENT to a revealed dora count as HALF a dora; every 2 such
    // tiles = +1 han (floor). Absent == OFF (NOT in defaultRuleSet), so default scoring is byte-identical.
    doraNeighbors: z7.boolean().optional()
  }),
  flags: z7.object({
    openRiichi: z7.boolean(),
    kuitan: z7.boolean(),
    atozuke: z7.enum(["allow", "forbid", "completedOnly"]),
    multiWinPolicy: z7.enum(["headBump", "doubleRon", "tripleRon"]),
    sanmaKitaMode: z7.enum(["nukiDora", "normalHonor", "unused"]),
    // 抜きドラ対象牌: the set of honors a seat may 抜く (nuki) as bonus dora when sanmaKitaMode==='nukiDora'.
    // Absent => default ['z4'] (North only / 北抜き). 二麻 (2-player) can set ['z3','z4'] to also pull West
    // (西抜き). Additive — absent == current behavior (North-only), so the 4-player path is unaffected.
    nukiTiles: z7.array(TileIdSchema).optional(),
    tsumoLoss: z7.boolean().optional(),
    renchan: z7.enum(["agari", "tenpai", "none"]),
    kuikae: z7.enum(["none", "strict", "loose"]),
    nagashiMangan: z7.boolean(),
    tobi: z7.boolean().optional(),
    // game ends when any seat busts (score < 0)
    noCalls: z7.boolean().optional(),
    // 鳴きなし: forbid pon/chii/大明槓/加槓 (ankan & ron still OK; menzen-forced)
    noChii: z7.boolean().optional(),
    // 三麻チー禁止: forbid chii ONLY (pon/kan/ron stay allowed); narrower than noCalls
    // 無限立直 (kamisama-mahjong style). Additive; absent == current behavior (NOT in defaultRuleSet).
    riichiBelowStick: z7.boolean().optional(),
    // 点棒なし立直: allow declaring riichi even when score < riichiStick (stick is still posted; score may go negative — intended; tobi handling unchanged)
    reRiichi: z7.boolean().optional(),
    // 再立直: allow declaring riichi AGAIN while already in riichi (re-arms ippatsu, posts another stick, keeps tenpai)
    infiniteRiichi: z7.boolean().optional(),
    // 無限立直: offer 無限/オープン無限 modes — lifts the tsumogiri-lock (change your wait) and auto-drains 1000/own-draw into the pot; implies below-stick (can go negative) and disables トビ while enabled
    // 自元 (じげん) 席役牌: OPT-IN. Assign seats >=4 (the 5th-7th seats) a dragon role tile
    // (rel4=白 rel5=發 rel6=中) that scores +1 ADDITIVE han when completed — your assigned dragon is
    // worth 2 han (三元牌 1 + 自元 1), others' stays 1. Absent (default) == OFF == byte-identical wall
    // & scoring (golden digest 3c70e86d unchanged). Season/flower seat-role families are Phase3.
    seatDragon: z7.boolean().optional()
  }),
  points: z7.object({ start: z7.number().int(), return: z7.number().int(), uma: z7.array(z7.number().int()), oka: z7.number().int(), honbaValue: z7.number().int(), riichiStick: z7.number().int() }),
  time: z7.object({
    baseSeconds: z7.number().int(),
    perTurnSeconds: z7.number().int(),
    // 再接続猶予 (online only; engine never reads this). Absent ⇒ legacy behaviour: a dropped socket goes
    // tsumogiri-bot IMMEDIATELY. When present, onSocketGone first flags the seat OFFLINE (badge) for
    // `badgeMs`, then — if still gone after a further `tsumogiriMs` — makes it a tsumogiri bot. Additive &
    // opt-in: NOT in defaultRuleSet (golden digest 3c70e86d unchanged).
    reconnectGrace: z7.object({ badgeMs: z7.number().int(), tsumogiriMs: z7.number().int() }).optional()
  }),
  // 対局長 (flow.md l.347-348): one局 / 東風 / 半荘 / 無限. OPT-IN, DEFAULT-ABSENT.
  // Absent == 'infinite' (current behavior: トビまで無限進行) so existing rulesets' ruleSetHash is
  // UNCHANGED and N<=4 stays byte-identical. This is a PLANNING field consumed by pure engine
  // helpers (gamelength.ts); it does NOT yet drive the multi-hand loop's game-over (later hole).
  gameLength: z7.enum(["single", "tonpuu", "hanchan", "infinite"]).optional()
});
function ruleSetHash(rs) {
  return fnv1a32(stableStringify(rs));
}
function nSymmetricUma(players, topUma = 15) {
  const n = players;
  if (n <= 1) return [0];
  const u = new Array(n).fill(0);
  for (let r = 0; r < Math.floor(n / 2); r++) {
    const v = Math.round(topUma * (n - 1 - 2 * r) / (n - 1));
    u[r] = v;
    u[n - 1 - r] = -v;
  }
  return u;
}
function defaultRuleSet(players) {
  return {
    version: RULESET_SCHEMA_VERSION,
    players,
    tiles: { suits: ["m", "p", "s"], honors: ["E", "S", "W", "N", "P", "F", "C"], akaCount: { m: 1, p: 1, s: 1 } },
    enabledYaku: {},
    // P1a seeds the catalog; P0 only validates the shape
    // `limitMode` is intentionally OMITTED here (absent => effective 'capped') so a test/room that sets
    // the legacy `aotenjou: true` still resolves to 'aotenjou'. Pick 'stepped'/'aotenjou' explicitly.
    scoring: { kazoeYakumanCap: 13, kiriageMangan: false, doubleYakuman: true, uraDora: true, kanDora: true, hanRequirement: 1, doubleWindPairFu: 4 },
    flags: { openRiichi: false, kuitan: true, atozuke: "allow", multiWinPolicy: "headBump", sanmaKitaMode: players <= 3 ? "nukiDora" : "unused", renchan: "agari", kuikae: "strict", nagashiMangan: true, tobi: true },
    // uma/oka are placement bonuses (irrelevant for solo). N-symmetric, total-0 uma for any player count.
    points: { start: 25e3, return: 3e4, uma: nSymmetricUma(players), oka: 2e4, honbaValue: 300, riichiStick: 1e3 },
    time: { baseSeconds: 5, perTurnSeconds: 10 }
  };
}
function withPlayers(rs, players) {
  const keepNormal = rs.flags.sanmaKitaMode === "normalHonor";
  const sanmaKitaMode = players <= 3 ? keepNormal ? "normalHonor" : "nukiDora" : "unused";
  return { ...rs, players, flags: { ...rs.flags, sanmaKitaMode } };
}

// src/score.ts
import { z as z8 } from "zod";
var FuBreakdownSchema = z8.object({
  total: z8.number().int(),
  items: z8.array(z8.object({ reason: z8.string(), fu: z8.number().int() }))
});
var ScoreResultSchema = z8.object({
  yaku: z8.array(z8.object({ id: z8.string(), han: z8.number().int() })),
  doraBreakdown: z8.object({ dora: z8.number().int(), aka: z8.number().int(), ura: z8.number().int(), kita: z8.number().int(), flower: z8.number().int(), neighbor: z8.number().int() }),
  fu: z8.number().int(),
  han: z8.number().int(),
  yakuman: z8.number().int(),
  points: z8.number().int(),
  payments: z8.array(z8.number().int()),
  pao: z8.object({ seat: z8.number().int(), share: z8.number() }).optional(),
  honbaAdded: z8.number().int(),
  breakdown: FuBreakdownSchema
});

// src/seatTicket.ts
import { z as z9 } from "zod";
var SeatTicketClaimsSchema = z9.object({
  tableId: z9.string(),
  userId: z9.string(),
  seat: SeatSchema,
  ruleSetHash: z9.string(),
  nonce: z9.string(),
  jti: z9.string(),
  iat: z9.number().int(),
  exp: z9.number().int()
});
var SEAT_TICKET_TTL_MS = 6e4;
var enc = new TextEncoder();
var dec = new TextDecoder();
function b64urlEncode(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlDecode(s) {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - s.length % 4);
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
async function importHmacKey(secret) {
  return crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}
async function signSeatTicket(claims, secret) {
  const body = b64urlEncode(enc.encode(stableStringify(claims)));
  const key = await importHmacKey(secret);
  const sig = new Uint8Array(await crypto.subtle.sign("HMAC", key, enc.encode(body)));
  return body + "." + b64urlEncode(sig);
}
async function verifySeatTicket(token, secret, nowMs) {
  const dot = token.indexOf(".");
  if (dot < 0) return { ok: false, code: "BAD_SEAT_TICKET" };
  const body = token.slice(0, dot);
  const sig = token.slice(dot + 1);
  const key = await importHmacKey(secret);
  let valid;
  try {
    valid = await crypto.subtle.verify("HMAC", key, b64urlDecode(sig), enc.encode(body));
  } catch {
    return { ok: false, code: "BAD_SEAT_TICKET" };
  }
  if (!valid) return { ok: false, code: "BAD_SEAT_TICKET" };
  let parsed;
  try {
    parsed = JSON.parse(dec.decode(b64urlDecode(body)));
  } catch {
    return { ok: false, code: "BAD_SEAT_TICKET" };
  }
  const claims = SeatTicketClaimsSchema.safeParse(parsed);
  if (!claims.success) return { ok: false, code: "BAD_SEAT_TICKET" };
  if (nowMs >= claims.data.exp) return { ok: false, code: "TICKET_EXPIRED" };
  return { ok: true, claims: claims.data };
}

// src/seed.ts
function splitmix32(x) {
  let z13 = x + 2654435769 >>> 0;
  z13 = Math.imul(z13 ^ z13 >>> 16, 569420461) >>> 0;
  z13 = Math.imul(z13 ^ z13 >>> 15, 1935289751) >>> 0;
  return (z13 ^ z13 >>> 15) >>> 0;
}
function bytesToLanes(seed) {
  if (seed.length !== 32) throw new Error("seed must be 32 bytes");
  const dv = new DataView(seed.buffer, seed.byteOffset, seed.byteLength);
  const w = Array.from({ length: 8 }, (_, i) => dv.getUint32(i * 4, true));
  let acc = 2654435769 >>> 0;
  const mixLane = (a, b) => {
    acc = acc + splitmix32((a ^ b) >>> 0) >>> 0;
    return splitmix32(acc);
  };
  let lanes = [
    mixLane(w[0], w[4]),
    mixLane(w[1], w[5]),
    mixLane(w[2], w[6]),
    mixLane(w[3], w[7])
  ];
  if (lanes.every((x) => x === 0)) lanes = [1, 0, 0, 0];
  return lanes;
}
function hexToSeed(hex) {
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}
function seedToHex(seed) {
  return Array.from(seed, (b) => b.toString(16).padStart(2, "0")).join("");
}
function seedFromNumber(n) {
  const seed = new Uint8Array(32);
  let x = n >>> 0 || 1;
  for (let i = 0; i < 32; i++) {
    x ^= x << 13;
    x >>>= 0;
    x ^= x >> 17;
    x ^= x << 5;
    x >>>= 0;
    seed[i] = x & 255;
  }
  return seed;
}

// src/state.ts
import { z as z10 } from "zod";
var RiichiSchema = z10.object({ declared: z10.boolean(), ippatsu: z10.boolean(), double: z10.boolean(), open: z10.boolean(), turn: z10.number().int(), infinite: z10.boolean().optional() }).nullable();
var PhaseSchema = z10.enum(["deal", "draw", "discard", "callWindow", "resolve", "end"]);
var GameStateViewSchema = z10.object({
  wallCount: z10.number().int(),
  deadWallCount: z10.number().int(),
  doraIndicators: z10.array(TileIdSchema),
  uraDoraIndicators: z10.array(TileIdSchema).nullable(),
  roundWind: TileIdSchema,
  dealer: SeatSchema,
  turn: SeatSchema,
  phase: PhaseSchema,
  honba: z10.number().int(),
  riichiPot: z10.number().int(),
  players: z10.array(z10.object({
    seat: SeatSchema,
    melds: z10.array(MeldSchema),
    discards: z10.array(DiscardEntrySchema),
    riichi: RiichiSchema,
    score: z10.number().int(),
    seatWind: TileIdSchema.nullable(),
    kitaCount: z10.number().int(),
    flowerCount: z10.number().int().optional(),
    // 花牌 set-aside count (OPT-IN; absent/0 when flowers OFF)
    flowers: z10.array(TileIdSchema).optional(),
    // 花牌 set-aside TILES (face-up; OPT-IN; absent when flowers OFF)
    furitenSelf: z10.boolean().optional(),
    hand: z10.array(TileIdSchema).optional(),
    // own seat only
    handCount: z10.number().int().optional()
    // others only
  }))
});
var PlayerStateSchema = z10.object({
  seat: SeatSchema,
  hand: z10.array(TileIdSchema),
  melds: z10.array(MeldSchema),
  discards: z10.array(DiscardEntrySchema),
  riichi: z10.object({ declared: z10.boolean(), ippatsu: z10.boolean(), double: z10.boolean(), open: z10.boolean(), turn: z10.number().int(), infinite: z10.boolean().optional() }).nullable(),
  furiten: z10.object({ temporary: z10.boolean(), permanent: z10.boolean(), byRiichi: z10.boolean() }),
  score: z10.number().int(),
  seatWind: TileIdSchema.nullable(),
  // 自元 (席役牌, OPT-IN flags.seatDragon): the dragon role tile for seats >=4 (rel4=白 z5, rel5=發 z6,
  // rel6=中 z7). OPTIONAL for back-compat with persisted states that predate this field. Read as `?? null`.
  seatDragon: TileIdSchema.nullable().optional(),
  kitaCount: z10.number().int(),
  flowerCount: z10.number().int(),
  flowers: z10.array(TileIdSchema),
  nagashiAlive: z10.boolean(),
  // 立直ツモ切りロック用の一時フィールド: this turn's drawn/replacement/rinshan tile (the only tile a
  // riichi'd seat may discard). Transient — set on every tile pulled from a wall, cleared on discard, and
  // intentionally EXCLUDED from gameStateHash (replay.ts) so it never affects the determinism digest.
  drawnThisTurn: TileIdSchema.nullable().optional()
});

// src/wire.ts
import { z as z11 } from "zod";
var PROTOCOL_VERSION = 1;
var ENGINE_VERSION = "1";
var ErrCodeSchema = z11.enum([
  "PROTOCOL_MISMATCH",
  "AUTH_REQUIRED",
  "BAD_SEAT_TICKET",
  "TICKET_EXPIRED",
  "TICKET_REPLAY",
  "SEAT_TAKEN",
  "NOT_YOUR_TURN",
  "NOT_HOST",
  "ILLEGAL_MOVE",
  "ROOM_NOT_FOUND",
  "ROOM_FULL",
  "ROOM_STARTED",
  "RATE_LIMITED",
  "INTERNAL"
]);
var CapsSchema = z11.object({
  protocolVersion: z11.number().int(),
  rulesetSchemaVersion: z11.number().int(),
  catalogSchemaVersion: z11.number().int(),
  engineVersion: z11.string(),
  movelogFormat: z11.number().int()
});
function localCaps() {
  return {
    protocolVersion: PROTOCOL_VERSION,
    rulesetSchemaVersion: RULESET_SCHEMA_VERSION,
    catalogSchemaVersion: CATALOG_SCHEMA_VERSION,
    engineVersion: ENGINE_VERSION,
    movelogFormat: MOVELOG_FORMAT
  };
}
function isCompatible(client, server) {
  if (client.protocolVersion !== server.protocolVersion)
    return { ok: false, code: "PROTOCOL_MISMATCH", reason: `protocol ${client.protocolVersion} vs ${server.protocolVersion}` };
  if (client.movelogFormat !== server.movelogFormat)
    return { ok: false, code: "PROTOCOL_MISMATCH", reason: `movelog ${client.movelogFormat} vs ${server.movelogFormat}` };
  if (server.rulesetSchemaVersion < client.rulesetSchemaVersion)
    return { ok: false, code: "PROTOCOL_MISMATCH", reason: `ruleset schema server ${server.rulesetSchemaVersion} < client ${client.rulesetSchemaVersion}` };
  if (server.catalogSchemaVersion < client.catalogSchemaVersion)
    return { ok: false, code: "PROTOCOL_MISMATCH", reason: `catalog schema server ${server.catalogSchemaVersion} < client ${client.catalogSchemaVersion}` };
  return { ok: true };
}
var MeldKindSchema = z11.enum([...MELD_KINDS]);
var GameEventWireSchema = z11.intersection(
  z11.object({ seq: z11.number().int() }),
  z11.discriminatedUnion("event", [
    z11.object({ event: z11.literal("deal") }),
    z11.object({ event: z11.literal("draw"), seat: SeatSchema, tile: TileIdSchema.optional() }),
    z11.object({ event: z11.literal("discard"), seat: SeatSchema, tile: TileIdSchema, tsumogiri: z11.boolean() }),
    z11.object({ event: z11.literal("call"), seat: SeatSchema, kind: MeldKindSchema, tiles: z11.array(TileIdSchema), from: SeatSchema.nullable() }),
    z11.object({ event: z11.literal("riichi"), seat: SeatSchema, tile: TileIdSchema }),
    z11.object({ event: z11.literal("doraFlip"), indicator: TileIdSchema }),
    z11.object({ event: z11.literal("flower"), seat: SeatSchema, tile: TileIdSchema }),
    z11.object({ event: z11.literal("win"), winners: z11.array(SeatSchema), results: z11.array(ScoreResultSchema), hands: z11.array(z11.array(TileIdSchema)), ura: z11.array(TileIdSchema) }),
    z11.object({ event: z11.literal("ryuukyoku"), kind: z11.string(), tenpai: z11.array(SeatSchema), payments: z11.array(z11.number().int()) })
  ])
);
var WaitKindSchema = z11.enum(["turn", "call", "handOver", "gameOver"]);
var StampSchema = z11.enum([
  "hello",
  "thanks",
  "sorry",
  "nice",
  "gg",
  "oops",
  "think",
  "wait",
  "wow",
  "lol"
]);
var STAMP_THROTTLE_MS = 2e3;
var SeatListSchema = z11.array(z11.object({
  seat: SeatSchema,
  occupant: z11.enum(["human", "bot", "empty"]),
  displayName: z11.string().nullable(),
  // true while a human seat's socket has dropped but is still inside the reconnect grace (badge phase):
  // the seat is NOT yet a bot and the table still waits on it. Absent/false ⇒ connected or already a bot.
  offline: z11.boolean().optional()
}));
var RoomStateSchema = z11.object({
  roomCode: z11.string(),
  tableId: z11.string(),
  players: z11.number().int(),
  botFill: z11.boolean(),
  // false while the room is in the waiting lobby (config registered, no match yet); true once the host
  // started the match (deals/alarm armed). Lets a reconnecting client tell waiting from in-progress.
  started: z11.boolean(),
  // the controlling seat: only this seat may `updateConfig`/`start`. Reassigned (host-transfer) when the
  // current host leaves/drops → the lowest-numbered still-occupied seat. Clients derive isHost = mySeat===host.
  host: SeatSchema,
  // the room's current ruleset (host-editable while waiting via `updateConfig`); surfaced so the waiting
  // lobby + a reconnecting client render the live config without a separate HTTP fetch.
  ruleSet: RuleSetSchema,
  seats: SeatListSchema
});
var RoomCreateReqSchema = z11.object({ ruleSet: RuleSetSchema, players: z11.number().int(), botFill: z11.boolean() });
var RoomJoinReqSchema = z11.object({ roomCode: z11.string() });
var RoomTicketRespSchema = z11.object({
  room: RoomStateSchema,
  seat: SeatSchema,
  seatTicket: z11.string(),
  wsUrl: z11.string(),
  ruleSet: RuleSetSchema
});
var ClientMsgSchema = z11.discriminatedUnion("t", [
  // first frame after WS open: present the seatTicket + negotiate. resume carries lastAckSeq on reconnect.
  z11.object({ t: z11.literal("hello"), protocolVersion: z11.number().int(), seatTicket: z11.string(), resume: z11.object({ lastAckSeq: z11.number().int() }).optional() }),
  // a game move. Intent is the EXISTING seatless IntentSchema; clientSeq = at-least-once idempotency id.
  z11.object({ t: z11.literal("intent"), clientSeq: z11.number().int(), intent: IntentSchema }),
  // flow-control / reconnect ack: highest wire seq the client has durably applied.
  z11.object({ t: z11.literal("ack"), lastAckSeq: z11.number().int() }),
  // acknowledge a hand-over result → advance to the next hand (server may also auto-advance on a timer).
  z11.object({ t: z11.literal("ready") }),
  // host-only (seat 0): leave the waiting lobby and start the match. botFill fills any empty seats with
  // tsumogiri bots; the server rejects this from any other seat (NOT_HOST) and guards a double-start.
  z11.object({ t: z11.literal("start"), botFill: z11.boolean() }),
  // host-only, WAITING only: mutate the room's player count and/or ruleset before the match starts. The
  // server rejects from a non-host (NOT_HOST) / once started (ROOM_STARTED) and re-broadcasts roomState.
  z11.object({ t: z11.literal("updateConfig"), players: z11.number().int().min(1).max(7).optional(), ruleSet: RuleSetSchema.optional() }),
  // graceful per-seat leave (NEVER disbands the room): frees the sender's seat; if the sender was the host,
  // host transfers to the lowest still-occupied seat. Distinct from a socket drop (which keeps a reconnect grace).
  z11.object({ t: z11.literal("leave") }),
  // preset emote: a fixed-enum stamp, broadcast to all seats. Server-throttled (STAMP_THROTTLE_MS).
  z11.object({ t: z11.literal("stamp"), stamp: StampSchema }),
  z11.object({ t: z11.literal("ping"), ts: z11.number().int() })
]);
var ServerMsgSchema = z11.discriminatedUnion("t", [
  // negotiation result (sent before seatAssigned). If !ok the server closes after.
  z11.object({ t: z11.literal("welcome"), ok: z11.boolean(), serverCaps: CapsSchema, error: ErrCodeSchema.optional() }),
  z11.object({ t: z11.literal("seatAssigned"), seat: SeatSchema, tableId: z11.string(), players: z11.number().int() }),
  // full redacted state for THIS viewer + the viewer-only drawPeek (canonical: it lives HERE, not in the
  // GameStateView — see wire.ts header & the P3 plan). `wait` = what this viewer must do; `legalCalls` =
  // their legal intents during a turn/call window (depends on furiten/discard — not derivable from the view).
  z11.object({ t: z11.literal("snapshot"), seq: z11.number().int(), state: GameStateViewSchema, drawPeek: TileIdSchema.optional(), wait: WaitKindSchema, legalCalls: z11.array(IntentSchema).optional() }),
  z11.object({ t: z11.literal("event"), seq: z11.number().int(), ev: GameEventWireSchema }),
  // one reduce() batch applied atomically; seq = the LAST event's wire seq.
  z11.object({ t: z11.literal("eventBatch"), seq: z11.number().int(), evs: z11.array(GameEventWireSchema) }),
  // journal markers mirroring the studio EventJournal so the client builds an identical timeline for fx.
  z11.object({ t: z11.literal("handStart"), seq: z11.number().int(), kyoku: z11.number().int(), dealer: SeatSchema, honba: z11.number().int(), roundWind: TileIdSchema, scores: z11.array(z11.number().int()), doraIndicators: z11.array(TileIdSchema) }),
  z11.object({ t: z11.literal("handEnd"), seq: z11.number().int(), result: z11.enum(["win", "ryuukyoku"]), winners: z11.array(SeatSchema), deltas: z11.array(z11.number().int()), scores: z11.array(z11.number().int()) }),
  z11.object({ t: z11.literal("gameOver"), seq: z11.number().int(), reason: z11.enum(["tobi", "endOfGame", "abort"]), scores: z11.array(z11.number().int()) }),
  // spec §8.3: a fixed-shape choices broadcast to ALL seats each discard so call-ability can't leak via
  // message presence/latency. `legal` is that seat's legal intents (≥ [{kind:'pass'}]).
  z11.object({ t: z11.literal("choices"), seq: z11.number().int(), deadline: z11.number().int(), legal: z11.array(IntentSchema) }),
  z11.object({ t: z11.literal("roomState"), room: RoomStateSchema }),
  // a preset stamp from `seat`, fanned out to ALL seats (incl. mid-hand). No wire seq: volatile, not
  // part of the ack/replay timeline (deliberately NOT seq'd — it must never be replayed on reconnect).
  z11.object({ t: z11.literal("stamp"), seat: SeatSchema, stamp: StampSchema }),
  z11.object({ t: z11.literal("error"), code: ErrCodeSchema, detail: z11.string().optional(), fatal: z11.boolean() }),
  z11.object({ t: z11.literal("pong"), ts: z11.number().int() })
]);

// src/yaku.ts
import { z as z12 } from "zod";
var StructuralConstraintSchema = z12.lazy(
  () => z12.union([
    z12.object({ form: z12.enum(["standard", "chiitoi", "kokushi"]) }),
    z12.object({ allTilesIn: z12.object({ suit: z12.enum(["m", "p", "s"]).optional(), rankRange: z12.tuple([z12.number(), z12.number()]).optional(), honors: z12.boolean().optional(), isYaochuu: z12.boolean().optional() }) }),
    z12.object({ groups: z12.object({ kind: z12.enum(["sequence", "triplet", "quad", "set"]).optional(), suit: z12.enum(["m", "p", "s"]).optional(), honor: z12.enum(["wind", "dragon"]).optional(), open: z12.boolean().optional(), atLeast: z12.number().int().optional(), atMost: z12.number().int().optional() }) }),
    z12.object({ identicalSequences: z12.object({ atLeast: z12.number().int().optional(), pairsAtLeast: z12.number().int().optional() }) }),
    z12.object({ consecutiveTriplets: z12.object({ atLeast: z12.number().int() }) }),
    z12.object({ sameSequenceSuits: z12.object({ atLeast: z12.number().int() }) }),
    z12.object({ consecutiveSequences: z12.object({ atLeast: z12.number().int() }) }),
    z12.object({ everyGroupTouches: z12.object({ terminal: z12.boolean().optional(), honor: z12.boolean().optional(), yaochuu: z12.boolean().optional() }) }),
    z12.object({ pair: z12.object({ suit: z12.enum(["m", "p", "s"]).optional(), honor: z12.enum(["wind", "dragon"]).optional(), isYaochuu: z12.boolean().optional(), rankRange: z12.tuple([z12.number(), z12.number()]).optional() }) }),
    z12.object({ winTile: z12.object({ in: z12.array(TileIdSchema).optional(), suit: z12.enum(["m", "p", "s"]).optional(), rankRange: z12.tuple([z12.number(), z12.number()]).optional(), isYaochuu: z12.boolean().optional() }) }),
    z12.object({ wait: z12.object({ in: z12.array(z12.enum(["ryanmen", "kanchan", "penchan", "shanpon", "tanki", "none"])) }) }),
    z12.object({ tilesSubsetOf: z12.object({ tiles: z12.array(TileIdSchema) }) }),
    z12.object({ suitsUsed: z12.object({ gates: z12.boolean().optional(), numberSuits: z12.literal("one").optional(), honors: z12.enum(["required", "forbidden"]).optional() }) }),
    z12.object({ ctx: z12.object({ firstTurn: z12.boolean().optional(), tsumoOnly: z12.boolean().optional(), ronOnly: z12.boolean().optional(), menzenOnly: z12.boolean().optional(), afterKan: z12.boolean().optional(), lastTile: z12.enum(["haitei", "houtei"]).optional(), nonDealer: z12.boolean().optional() }) }),
    z12.object({ all: z12.array(StructuralConstraintSchema) }),
    z12.object({ any: z12.array(StructuralConstraintSchema) }),
    z12.object({ none: z12.array(StructuralConstraintSchema) })
  ])
);
var YakuDefMetaSchema = z12.object({
  id: z12.string(),
  name: z12.object({ ja: z12.string(), en: z12.string() }),
  kind: z12.enum(["normal", "yakuman"]),
  defaultHan: z12.number().int(),
  tunable: z12.enum(["tunable", "structural"]),
  // 'dora' rows (e.g. 赤ドラ) are NOT real yaku: they only contribute han and never satisfy the
  // "must have >=1 yaku" gate. They live in the catalog so the owner can toggle/tune them.
  category: z12.enum(["standard", "localFamous", "dora"]),
  detector: z12.union([
    z12.object({ type: z12.literal("builtin"), key: z12.string() }),
    z12.object({ type: z12.literal("dsl"), constraint: StructuralConstraintSchema })
  ]),
  yakumanMult: z12.number().int().optional(),
  exampleHand: z12.array(TileIdSchema),
  description: z12.object({ ja: z12.string(), en: z12.string() }),
  source: z12.string().optional(),
  // Optional name shown when this yaku is scored as a DOUBLE (mult>=2) — e.g. 四暗刻 -> 四暗刻単騎,
  // 国士無双 -> 国士無双十三面待ち, 九蓮宝燈 -> 純正九蓮宝燈. Data (editable), not hardcoded in the UI.
  doubleName: z12.object({ ja: z12.string(), en: z12.string() }).optional(),
  // 食い下がり (kuisagari): han when the hand is NOT concealed (open). Used by DSL normal yaku; the
  // engine applies it before any room hanOverride.
  openHan: z12.number().int().optional(),
  // yaku ids this yaku SUBSUMES (drops when both fire). Replaces the engine's hardcoded exclusion
  // tables — exclusions are now DATA on the catalog.
  excludes: z12.array(z12.string()).optional()
});
export {
  ALL_INTENT_SAMPLES,
  ALL_TILE_IDS,
  CATALOG_SCHEMA_VERSION,
  CapsSchema,
  ClientMsgSchema,
  DetectionContextSchema,
  DiscardEntrySchema,
  ENGINE_VERSION,
  ErrCodeSchema,
  FuBreakdownSchema,
  GameEventWireSchema,
  GameStateViewSchema,
  IntentSchema,
  LOCAL_ONLY_COSMETIC_KEYS,
  MELD_KINDS,
  MOVELOG_FORMAT,
  MeldSchema,
  PROTOCOL_VERSION,
  PlayerStateSchema,
  RULESET_SCHEMA_VERSION,
  RoomCreateReqSchema,
  RoomJoinReqSchema,
  RoomStateSchema,
  RoomTicketRespSchema,
  RuleSetSchema,
  SEAT_TICKET_TTL_MS,
  STAMP_THROTTLE_MS,
  SUITS,
  ScoreResultSchema,
  SeatListSchema,
  SeatSchema,
  SeatTicketClaimsSchema,
  ServerMsgSchema,
  SharedCosmeticSchema,
  StampSchema,
  StructuralConstraintSchema,
  TileIdSchema,
  WaitKindSchema,
  YakuDefMetaSchema,
  baseOfAka,
  bytesToLanes,
  compareTiles,
  defaultRuleSet,
  fnv1a32,
  hexToSeed,
  isAka,
  isCompatible,
  isFlower,
  isHonor,
  isSharedCosmetic,
  localCaps,
  nSymmetricUma,
  packMoves,
  ruleSetHash,
  seedFromNumber,
  seedToHex,
  signSeatTicket,
  stableStringify,
  unpackMoves,
  verifySeatTicket,
  withPlayers
};
